import { RouteProps } from 'routes/AppRouter'
import { useTranslation } from 'react-i18next'
import React, { ChangeEvent, useEffect, useState } from 'react'
import { navigate } from '@reach/router'
import { ROUTE_PATIENTS } from '../../routes/routes-constants'
import styles from './Editor.module.css'
import { RolesService } from '../../modules/users/services/RolesServices'
import { getUserContainer } from '../../container/user-module'
import {
  IUserService,
  LOGGED_USER_SERVICE_KEY,
  ROLES_SERVICE_KEY,
  USER_SERVICE_KEY,
} from '../../modules/users'
import { Role } from 'modules/users/models/Role'
import {
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  Typography,
  TextField,
  Box,
  RadioGroup,
  Radio,
  Chip,
  MenuItem,
  Modal,
} from '@mui/material'
import { emptyUserDTO, fromModel, User, UserDTO } from '../../modules/users/models/User'
import { DIET_TYPES, DIET_TEXTURES } from '../../common/utils/dietUtils'
import { Alert } from '@material-ui/lab'
import { ILoggedUserService } from 'modules/users/services/LoggedUserService'
import { getAppContainer, STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from '../../common/status/StatusService'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { IPatologyService, PATOLOGY_SERVICE_KEY } from '../../modules/patologies'
import { Query } from '../../common/api/Query'
import { getPatologyContainer } from '../../container/patologies-module'
import { Input } from '../../pages/user-profile/userProfileStylesMui'
import { InputTextDescription } from '../resource/userProfileStylesMui'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import calendarIcon from '../../assets/calendar/calendar-newEvent.svg'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { Subscription } from 'rxjs'
import { CustomModal } from '../../components/modal/CustomModal'
import { PageLayout } from 'pages/layout'

const OTHERS_PATHOLOGY_KEY = 'others'

const roleContainer = getUserContainer()
const roleService = roleContainer.get<RolesService>(ROLES_SERVICE_KEY)
const userContainer = getUserContainer()
const userService = userContainer.get<IUserService>(USER_SERVICE_KEY)
const patologyService = getPatologyContainer().get<IPatologyService>(PATOLOGY_SERVICE_KEY)
const loggedUserService = getUserContainer().get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
const statusService = getAppContainer().get<IStatusService>(STATUS_SERVICE_KEY)

interface EditorProps extends RouteProps {
  id?: string
}

const CalendarIcon = () => <img src={calendarIcon} alt="calendar" className={styles.calendarIcon} />

export function Editor(props: EditorProps) {
  const { t } = useTranslation()
  const loggedUser = loggedUserService.get()
  const [roles, setRoles] = useState<Role[]>([])
  const [patient, setPatient] = useState<UserDTO>(emptyUserDTO())
  const [originalPatient, setOriginalPatient] = useState<UserDTO>(emptyUserDTO())
  const [message, setMessage] = useState<string>()
  const [userLogged, setUserLogged] = useState<User>()
  const [selectedPatologies, setSelectedPatologies] = useState<string[]>([])
  const [possiblePatologies, setPossiblePatologies] = useState<string[]>([])
  const [translatePatologies, setTranslatePatologies] = useState(new Map<string, string>())
  const [DateTimePickerError, setDateTimePickerError] = useState<string>('')
  const [filteredDoctors, setFilteredDoctors] = useState<UserDTO[]>([])
  const [otherPatologyText, setOtherPatologyText] = useState<string>('')
  const [showDoctorChangeModal, setShowDoctorChangeModal] = useState<boolean>(false)

  const hasOthersSelected = selectedPatologies.some(
    (p) => translatePatologies.get(p) === OTHERS_PATHOLOGY_KEY || p === t(OTHERS_PATHOLOGY_KEY)
  )

  useEffect(() => {
    if (!loggedUser?.id) return
    const subscription = userService.getByID(loggedUser?.id).subscribe((lu) => {
      setUserLogged(lu)
    })
    return () => subscription.unsubscribe()
  }, [loggedUser?.id, userService])

  useEffect(() => {
    const loadDoctors = () => {
      userService.getUsersByRole('med').subscribe(
        (res) => {
          if (res && res.items) {
            const filtered = res.items.filter(
              (doctor) => doctor.email !== 'test-profesional@careme.com'
            )
            setFilteredDoctors(filtered)
            if (!props.id && loggedUser?.id) {
              const currentDoctor = filtered.find((d) => d.id === loggedUser.id)
              if (currentDoctor) {
                setPatient((prev) => ({ ...prev, assignedID: loggedUser.id }))
              }
            }
          }
        },
        (error) => console.error('Error loading doctors:', error)
      )
    }

    loadDoctors()

    const sub1 = patologyService.getFilteredList(new Query({})).subscribe((res) => {
      const pts: string[] = []
      const trans = new Map<string, string>()
      if (res) {
        for (let i = 0; i < res.count; i++) {
          pts.push(t(res.items[i].name))
          trans.set(t(res.items[i].name), res.items[i].name)
        }
      }
      setTranslatePatologies(trans)
      setPossiblePatologies(pts)
    })

    let sub2: Subscription | null = null
    if (props.id) {
      sub2 = userService.getByID(props.id).subscribe((res) => {
        if (res) {
          const dto = fromModel(res)
          setPatient(dto)
          setOriginalPatient(dto)
        }
      })
    }

    const rolesAux: Role[] = []
    const sub3 = roleService.getAll().subscribe((res) => {
      res.forEach((e) => {
        if (
          e.name === 'externProfessional' ||
          e.name === 'family/Tutor' ||
          e.name === 'carer/Teacher'
        ) {
          rolesAux.push(e)
        }
      })
      setRoles(rolesAux)
    })

    return () => {
      sub1.unsubscribe()
      sub2?.unsubscribe()
      sub3.unsubscribe()
    }
  }, [props.id, loggedUser?.id, t, userService, patologyService, roleService])

  useEffect(() => {
    if (patient.patologies && patient.patologies.length > 0 && translatePatologies.size > 0) {
      const translatedPats = patient.patologies
        .map((p) => {
          const entry = Array.from(translatePatologies.entries()).find(([_, orig]) => orig === p)
          return entry ? entry[0] : p
        })
        .filter((p) => p !== '')
      setSelectedPatologies(translatedPats)
    }
  }, [patient.patologies, translatePatologies])

  useEffect(() => {
    if (translatePatologies.size === 0) return
    if (!patient.patologies || patient.patologies.length === 0) return
    const othersIsSelected = patient.patologies.includes(OTHERS_PATHOLOGY_KEY)
    if (othersIsSelected && patient.otherPatology) {
      setOtherPatologyText(patient.otherPatology)
    }
  }, [translatePatologies, patient.patologies, patient.otherPatology])

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setPatient({ ...patient, [e.target.id]: e.target.value })
  }

  const handleAutocompleteChange = (pat: string[]) => {
    const trans: string[] = []
    pat.forEach((element: string) => {
      const original = translatePatologies.get(element)
      if (original) trans.push(original)
    })
    setPatient({ ...patient, patologies: trans })
    const stillHasOthers = pat.some((p) => translatePatologies.get(p) === OTHERS_PATHOLOGY_KEY)
    if (!stillHasOthers) setOtherPatologyText('')
  }

  const handleGender = (e: ChangeEvent<HTMLInputElement>) => {
    setPatient({ ...patient, gender: e.target.value === 'male' ? 1 : 2 })
  }

  const handleDoctorChange = (event: ChangeEvent<{ value: unknown }>) => {
    setPatient({ ...patient, assignedID: event.target.value as string })
  }

  const handleBack = () => navigate(ROUTE_PATIENTS)

  const validateForm = () => {
    if (
      !patient.email ||
      !patient.diagnosis ||
      !patient.dob ||
      !patient.gender ||
      !patient.lastName ||
      !patient.firstName ||
      !patient.historyRecordNumber ||
      !patient.diagnosisSummary ||
      !patient.assignedID ||
      patient.diet.types.length === 0 ||
      !patient.diet.texture
    )
      return false
    if (hasOthersSelected && !otherPatologyText.trim()) return false
    return true
  }

  const handleDateChange = (date: Date | null) => {
    if (date) setPatient({ ...patient, dob: date })
  }

  const checkIfDoctorChanged = () => {
    if (!props.id) return patient.assignedID && patient.assignedID !== loggedUser?.id
    return patient.assignedID !== originalPatient.assignedID
  }

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return
    if (checkIfDoctorChanged()) setShowDoctorChangeModal(true)
    else savePatient()
  }

  const savePatient = () => {
    const newPatient: UserDTO = {
      ...patient,
      language: userLogged?.language ?? patient.language,
      assignedID: patient.assignedID,
      isPatient: true,
      isActive: true,
      otherPatology: hasOthersSelected ? otherPatologyText.trim() : '',
    }

    if (props.id) {
      const subscription = userService.update(newPatient).subscribe(() => {
        statusService.sendStatus({ variant: 'success' })
        handleBack()
      })
      return () => subscription.unsubscribe()
    } else {
      const subscription = userService.add(newPatient, navigator.language).subscribe((res) => {
        if (res) {
          statusService.sendStatus({ variant: 'success' })
          handleBack()
        }
      })
      return () => subscription.unsubscribe()
    }
  }

  return (
    <PageLayout>
      <form className={styles.form} id={'editUser'} onSubmit={handleSave}>
        <Box>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('name')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.firstName}
                fullWidth
                id="firstName"
                label={''}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('lastName')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.lastName}
                id="lastName"
                label={''}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('email')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.email}
                id="email"
                label={''}
                variant="outlined"
                required
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('patologies')}</Typography>
              <Autocomplete
                multiple
                id="patologies"
                options={possiblePatologies}
                value={selectedPatologies}
                onChange={(_, v) => {
                  setSelectedPatologies(v)
                  handleAutocompleteChange(v)
                }}
                ChipProps={{ onDelete: undefined }}
                className={styles.autocomplete}
                renderInput={(params) => (
                  <TextField {...params} variant="outlined" className={styles.autocompleteInput} />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      label={option}
                      {...getTagProps({ index })}
                      className={styles.chip}
                      onDelete={() => {
                        const newSelected = selectedPatologies.filter((_, i) => i !== index)
                        setSelectedPatologies(newSelected)
                        handleAutocompleteChange(newSelected)
                      }}
                    />
                  ))
                }
                noOptionsText={t('noElementsAvailable')}
                ListboxProps={{ style: { maxHeight: '250px' } }}
              />
              {hasOthersSelected && (
                <Box mt={1}>
                  <Input
                    sx={{ width: '100%' }}
                    id="otherPatologyText"
                    label={''}
                    placeholder={t('otherPathologyPlaceholder')}
                    variant="outlined"
                    value={otherPatologyText}
                    required={hasOthersSelected}
                    onChange={(e) => setOtherPatologyText(e.target.value)}
                  />
                </Box>
              )}
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <FormControl sx={{ width: '100%' }}>
                <FormLabel className={styles.inputTitle} id="gender">
                  {t('gender')}
                </FormLabel>
                <RadioGroup
                  row
                  aria-labelledby="gender"
                  name="gender"
                  value={patient.gender === 1 ? 'male' : 'female'}
                  onChange={(e) => handleGender(e)}
                >
                  <FormControlLabel
                    value="male"
                    control={<Radio />}
                    className={styles.radioGroup}
                    label={t('male')}
                  />
                  <FormControlLabel
                    value="female"
                    control={<Radio />}
                    className={styles.radioGroup}
                    label={t('female')}
                  />
                </RadioGroup>
              </FormControl>
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('historyNumber')}</Typography>
              <Input
                sx={{ width: '100%' }}
                id="historyRecordNumber"
                label={''}
                value={patient.historyRecordNumber || ''}
                variant="outlined"
                required
                type="number"
                inputProps={{ min: 0 }}
                onChange={(e) =>
                  setPatient({ ...patient, historyRecordNumber: Number(e.target.value) })
                }
              />
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box} display={'flex'} gap={2}>
              <Box flex={1}>
                <Typography className={styles.inputTitle}>{t('dietTypes')}</Typography>
                <Autocomplete
                  multiple
                  id="diet-types"
                  options={DIET_TYPES.map((d) => d)}
                  getOptionLabel={(option) => t(option)}
                  value={patient.diet.types}
                  onChange={(_, v) =>
                    setPatient({ ...patient, diet: { ...patient.diet, types: v } })
                  }
                  ChipProps={{ onDelete: undefined }}
                  className={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      className={styles.autocompleteInput}
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        label={t(option)}
                        {...getTagProps({ index })}
                        className={styles.chip}
                        onDelete={() => {
                          const updated = patient.diet.types.filter((_, i) => i !== index)
                          setPatient({ ...patient, diet: { ...patient.diet, types: updated } })
                        }}
                      />
                    ))
                  }
                  noOptionsText={t('noElementsAvailable')}
                  ListboxProps={{ style: { maxHeight: '250px' } }}
                />
              </Box>
              <Box flex={1}>
                <Typography className={styles.inputTitle}>{t('dietTexture')}</Typography>
                <Autocomplete
                  id="diet-texture"
                  options={DIET_TEXTURES.map((tx) => tx)}
                  getOptionLabel={(option) => t(option)}
                  value={patient.diet.texture || null}
                  onChange={(_, v) =>
                    setPatient({ ...patient, diet: { ...patient.diet, texture: v ?? '' } })
                  }
                  className={styles.autocomplete}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      className={styles.autocompleteInput}
                    />
                  )}
                  noOptionsText={t('noElementsAvailable')}
                  ListboxProps={{ style: { maxHeight: '250px' } }}
                />
              </Box>
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('birthDate')}</Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={'es'}>
                <DatePicker
                  value={patient.dob}
                  inputFormat={'DD/MM/YYYY'}
                  onChange={handleDateChange}
                  label={''}
                  onError={(reason) => {
                    if (reason === 'invalidDate') setDateTimePickerError(t('invalidDateMessage'))
                    if (reason === 'minDate') setDateTimePickerError(t('minDateMessage'))
                  }}
                  renderInput={(params) => (
                    <TextField
                      sx={{ width: '100%' }}
                      id="dob"
                      size={'small'}
                      helperText={params.error && DateTimePickerError}
                      {...params}
                    />
                  )}
                  components={{
                    OpenPickerIcon: CalendarIcon,
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('diagnosisSummary')}</Typography>
              <Input
                sx={{ width: '100%' }}
                value={patient.diagnosisSummary}
                id="diagnosisSummary"
                name="diagnosisSummary"
                label={''}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('assignedDoctor')}</Typography>
              <TextField
                select
                value={patient.assignedID || ''}
                onChange={handleDoctorChange}
                fullWidth
                size="small"
                required
              >
                <MenuItem value="" disabled>
                  {t('selectDoctor')}
                </MenuItem>
                {filteredDoctors.map((doctor) => (
                  <MenuItem key={doctor.id} value={doctor.id}>
                    {doctor.firstName} {doctor.lastName}
                  </MenuItem>
                ))}
              </TextField>
            </Box>
          </Box>

          <Box display={'flex'} justifyContent={'space-between'}>
            <Box className={styles.box}>
              <Typography className={styles.inputTitle}>{t('diagnosis')}</Typography>
              <InputTextDescription
                sx={{ width: '100%' }}
                multiline
                rows={6}
                value={patient.diagnosis}
                id="diagnosis"
                name="diagnosis"
                label={''}
                size={'medium'}
                variant="outlined"
                required={true}
                onChange={(e) => handleChange(e)}
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'row'}
              justifyContent={'center'}
              alignItems={'center'}
              className={styles.box}
            >
              <Box className={styles.buttons} mb={3} display="flex" justifyContent={'center'}>
                <Button className={styles.buttonCancel} type={'button'} onClick={handleBack}>
                  {t('back')}
                </Button>
                <Button className={styles.buttonAdd} type={'submit'}>
                  {props.id ? t('editIcon') : t('add')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </form>

      {message && (
        <Box mb={3}>
          <Alert className={styles.message} severity="success" key="message" id="message">
            {t(message)}
          </Alert>
        </Box>
      )}

      <Modal open={showDoctorChangeModal} onClose={() => setShowDoctorChangeModal(false)}>
        <CustomModal
          title={t('changeDoctorTitle')}
          warningText={
            checkIfDoctorChanged()
              ? props.id
                ? t('changeDoctorWarning')
                : t('createPatientForOtherDoctor')
              : ''
          }
          handleClose={() => setShowDoctorChangeModal(false)}
          handleSave={() => {
            setShowDoctorChangeModal(false)
            savePatient()
          }}
        />
      </Modal>
    </PageLayout>
  )
}
